import React, { useRef, useState, useMemo, useEffect } from 'react';
import { useBreadcrumbRoutes, Wrapper, Button, Modal, Select, TreeSelect, Empty } from '@maxtropy/components';
import { Tabs, Space } from 'antd';
import { useRequest } from 'ahooks';
import {
  apiV2HighEnergyDeviceAnalysisConfigDetailPost,
  apiV2HighEnergyDeviceAnalysisV2EnergyTrendPost,
  apiV2HighEnergyDeviceAnalysisRuntimeTrendPost,
  apiV2HighEnergyDeviceDeviceTreePost,
  apiV2HighEnergyDeviceAnalysisConfigSavePost,
  apiV2EnergyAnalysisGroupAndUnitPost,
  apiV2ExportCenterTaskHighEnergyDeviceAnalysisCreatePost,
} from '@maxtropy/device-customer-apis-v2';
import TimeSelector, { TimeSelectorRef, DateType } from './components/TimeSelector';
import StatInfo from './components/StatInfo';
import EnergyCard from './components/EnergyCard';
import ElectricityCard from './components/ElectricityCard';
import UsageRankCard from './components/UsageRankCard';
import CardBox from './components/CardBox';
import EnergyOverview from './components/EnergyOverview';
import EnergyStackChart from './components/EnergyStackChart';
import DurationCards from './components/DurationCards';
import RunStateChart from './components/RunStateChart';
import DeviceRankingList from './components/DeviceRankingList';
import styles from './index.module.scss';
import qs from 'qs';
import { useHasPermission } from '@/utils/utils';
import { PermissionsType } from '@/common/permissionsConst';
import { DoubleRightOutlined } from '@ant-design/icons';

// Tab菜单项类型
interface TabItem {
  key: string;
  label: string;
}

const HighEnergyDeviceAnalysis: React.FC = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const hasConfig = useHasPermission(PermissionsType.B_HIGHENERGYCONSUMPTIONEQUIPMENTANALYSISCONFIGURATION);
  const timeSelectorRef = useRef<TimeSelectorRef>(null);

  // 当前选中的菜单key
  const [activeKey, setActiveKey] = useState<string>('');

  // 配置弹窗状态
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [factoryElectricity, setFactoryElectricity] = useState<string | undefined>();

  // 全厂用电TreeSelect数据
  const [electricityTreeData, setElectricityTreeData] = useState<any[]>([]);

  // 获取设备树数据
  const { data: deviceTreeData } = useRequest(() => apiV2HighEnergyDeviceDeviceTreePost({}));
  // 导出
  const [showExportModal, setShowExportModal] = useState<boolean>(false);

  // 获取全厂用电树形数据
  useRequest(() => apiV2EnergyAnalysisGroupAndUnitPost({}), {
    onSuccess: res => {
      const treeData = formatToNodeData(res?.list || []);
      setElectricityTreeData(treeData);
    },
  });

  // 当设备树数据加载完成后，默认选中第一个tab
  useEffect(() => {
    if (deviceTreeData?.list && deviceTreeData.list.length > 0 && !activeKey) {
      setActiveKey(String(deviceTreeData.list[0].deviceTypeId));
    }
  }, [deviceTreeData, activeKey]);

  // 格式化树形数据
  function formatToNodeData(data: any[], level: number = 0): any[] {
    return data.map(i => {
      const children = i.children ?? i.energyUnits;
      const hasChildren = children && children.length > 0;

      return {
        title: i.name,
        value: i.energyUnitId ?? i.id,
        children: hasChildren ? formatToNodeData(children, level + 1) : [],
        selectable: level !== 0,
      };
    });
  }

  // 获取配置详情
  useRequest(() => apiV2HighEnergyDeviceAnalysisConfigDetailPost(), {
    onSuccess: data => {
      if (data?.energyUnitId) {
        setFactoryElectricity(String(data.energyUnitId));
      }
    },
  });

  // 时间参数状态
  const [timeParams, setTimeParams] = useState<{
    startTime: number;
    endTime: number;
    timeResolution: DateType;
  }>({
    startTime: 0,
    endTime: 0,
    timeResolution: DateType.MONTH,
  });

  // 获取能源趋势数据
  const { data: energyTrendData, loading: energyTrendLoading } = useRequest(
    () =>
      apiV2HighEnergyDeviceAnalysisV2EnergyTrendPost({
        startTime: timeParams.startTime,
        endTime: timeParams.endTime,
        timeResolution: timeParams.timeResolution,
        deviceTypeId: Number(activeKey),
      }),
    {
      ready: !!timeParams.startTime && !!timeParams.endTime && !!activeKey,
      refreshDeps: [timeParams.startTime, timeParams.endTime, timeParams.timeResolution, activeKey],
    }
  );

  // 获取运行趋势数据
  const { data: runtimeTrendData, loading: runtimeTrendLoading } = useRequest(
    () =>
      apiV2HighEnergyDeviceAnalysisRuntimeTrendPost({
        startTime: timeParams.startTime,
        endTime: timeParams.endTime,
        timeResolution: timeParams.timeResolution,
        deviceTypeId: Number(activeKey),
      }),
    {
      ready: !!timeParams.startTime && !!timeParams.endTime && !!activeKey,
      refreshDeps: [timeParams.startTime, timeParams.endTime, timeParams.timeResolution, activeKey],
    }
  );

  // Tab菜单项配置
  const tabItems: TabItem[] = useMemo(() => {
    if (deviceTreeData?.list && deviceTreeData.list.length > 0) {
      // 使用设备树数据构建tabs
      return deviceTreeData.list.map(item => ({
        key: String(item.deviceTypeId),
        label: item.deviceTypeName || '',
      }));
    } else {
      // 不返回默认tabs，而是显示Empty
      return [];
    }
  }, [deviceTreeData]);

  // Tab切换事件
  const handleTabChange = (key: string) => {
    setActiveKey(key);
    // 根据选中的key请求对应接口数据
    console.log('选中菜单:', key);
  };

  // 配置按钮点击
  const handleConfig = () => {
    setConfigModalVisible(true);
  };

  // 配置弹窗保存
  const handleConfigSave = () => {
    // 调用保存配置的接口
    apiV2HighEnergyDeviceAnalysisConfigSavePost({
      id: factoryElectricity,
    })
      .then(response => {
        // 接口调用成功后关闭模态框
        setConfigModalVisible(false);
        console.log('配置保存成功', response);
      })
      .catch(error => {
        console.error('配置保存失败', error);
      });
  };

  // 配置弹窗取消
  const handleConfigCancel = () => {
    setConfigModalVisible(false);
  };

  // 导出按钮点击
  const handleExport = () => {
    const { timeResolution, rangeTime } = timeSelectorRef.current || {};
    const params = {
      startTime: rangeTime?.startTime,
      endTime: rangeTime?.endTime,
      timeResolution: timeResolution,
    };

    apiV2ExportCenterTaskHighEnergyDeviceAnalysisCreatePost(params).then(res => {
      setShowExportModal(false);
      let successModal: ReturnType<typeof Modal.success> | null = null;
      successModal = Modal.success({
        title: '导出任务创建成功',
        content: (
          <div style={{ whiteSpace: 'nowrap' }}>
            请前往报表中心下载导出的报表，
            <Button
              type="link"
              style={{ padding: 0, gap: 2 }}
              onClick={() => {
                successModal?.destroy();
                window.open('/report/export/list', '_blank');
              }}
            >
              点击前往
              <DoubleRightOutlined />
            </Button>
          </div>
        ),
      });
    });

    // window.open(`/api/v2/high-energy/device/analysis/export` + qs.stringify(params, { addQueryPrefix: true }));
  };

  // 时间变化回调
  const handleTimeChange = (data: TimeSelectorRef) => {
    console.log('data', data);
    setTimeParams({
      startTime: data.rangeTime.startTime,
      endTime: data.rangeTime.endTime,
      timeResolution: data.timeResolution,
    });
  };

  return (
    <Wrapper className={styles.wrapper} routes={[...(breadcrumbRoutes?.routes ?? [])]}>
      <div className={styles.headerWrap}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <TimeSelector ref={timeSelectorRef} onChange={handleTimeChange} />
            <div className={styles.headerRight}>
              <Space size={8}>
                {hasConfig && (
                  <Button type="primary" onClick={handleConfig}>
                    配置
                  </Button>
                )}
                <Button type="primary" onClick={handleExport}>
                  导出
                </Button>
              </Space>
            </div>
          </div>
          <StatInfo timeParams={timeParams} />
        </div>
        <div className={styles.headerBottom}>
          <ElectricityCard timeParams={timeParams} />
          <EnergyCard timeParams={timeParams} />
          <UsageRankCard timeParams={timeParams} />
        </div>
      </div>
      <div className={styles.content}>
        <div className={styles.leftCol}>
          <div className={styles.tabsContainer}>
            {!tabItems || tabItems.length === 0 ? (
              <div className={styles.noData}>
                <Empty />
              </div>
            ) : (
              <Tabs
                tabPosition="left"
                activeKey={activeKey}
                className={styles.customTabs}
                onChange={handleTabChange}
                items={tabItems.map(item => ({
                  key: item.key,
                  label: item.label,
                }))}
              />
            )}
          </div>
        </div>
        <div className={styles.rightCol}>
          {activeKey ? (
            <CardBox title={tabItems.find(item => item.key === activeKey)?.label || ''}>
              {/* 卡片 */}
              <EnergyOverview data={energyTrendData} energyTrendLoading={energyTrendLoading} />
              {/* 图表区域  */}
              <EnergyStackChart
                data={energyTrendData}
                energyTrendLoading={energyTrendLoading}
                timeResolution={timeParams.timeResolution}
              />
              {/* 待机时长卡片 */}
              <DurationCards data={runtimeTrendData} runtimeTrendLoading={runtimeTrendLoading} />
              {/* 运行状态统计图表 */}
              <RunStateChart
                data={runtimeTrendData}
                runtimeTrendLoading={runtimeTrendLoading}
                timeResolution={timeParams.timeResolution}
              />
              {/* 设备排名列表 */}
              <DeviceRankingList timeParams={timeParams} deviceTypeId={activeKey ? Number(activeKey) : undefined} />
            </CardBox>
          ) : (
            <div className={styles.emptyRightCol}>
              <Empty />
            </div>
          )}
        </div>
      </div>
      <Modal
        contentClassName="modal-form-content"
        title="配置"
        open={configModalVisible}
        onOk={handleConfigSave}
        onCancel={handleConfigCancel}
        okText="保存"
        cancelText="取消"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>全厂用电：</span>
          <TreeSelect
            style={{ flex: 1 }}
            value={factoryElectricity}
            onChange={setFactoryElectricity}
            placeholder="请选择"
            treeData={electricityTreeData}
            allowClear
            showSearch
            treeNodeFilterProp="title"
            treeDefaultExpandAll
            className={styles['custom-tree-select']}
          />
        </div>
      </Modal>
    </Wrapper>
  );
};

export default HighEnergyDeviceAnalysis;
